import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BugIcon, DatabaseIcon, SearchIcon, PlusIcon, RefreshCwIcon,
  AlertTriangleIcon, CheckCircleIcon, ClockIcon, BookOpenIcon,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";

interface ProblemTicket {
  id: string;
  title: string;
  priority: string;
  status: string;
  assignee_name?: string;
  requester_name?: string;
  rca_status?: string;
  rca_id?: number;
  created_at: string;
}

interface KnownError {
  id: number;
  title: string;
  symptoms: string;
  workaround?: string;
  permanent_fix?: string;
  severity: string;
  status: string;
  source_problem?: string;
  created_by_name?: string;
  created_at: string;
  resolved_at?: string;
}

interface Stats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  kedb_active: number;
  rca_completed: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-800", high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800", low: "bg-green-100 text-green-800",
};
const SEVERITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-800", high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800", low: "bg-green-100 text-green-800",
};
const STATUS_LABELS: Record<string, string> = {
  open: "Aberto", in_progress: "Em Andamento", waiting_response: "Aguardando", resolved: "Resolvido", closed: "Fechado",
};
const fmtDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", year:"numeric" }) : "—";

const ProblemManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [problems, setProblems] = useState<ProblemTicket[]>([]);
  const [kedb, setKedb] = useState<KnownError[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, open: 0, in_progress: 0, resolved: 0, kedb_active: 0, rca_completed: 0 });
  const [loading, setLoading] = useState(true);
  const [kedbSearch, setKedbSearch] = useState("");
  const [showNewKE, setShowNewKE] = useState(false);
  const [newKE, setNewKE] = useState({
    title: "", symptoms: "", root_cause: "", workaround: "", permanent_fix: "",
    severity: "medium", source_problem: "",
  });

  const token = getToken();
  const user = getUser() ? JSON.parse(getUser()!) : null;
  const isInternal = user && (user.role === "admin" || user.role === "internal" || user.role === "analyst");

  useEffect(() => {
    if (!isInternal) { navigate("/"); return; }
    loadAll();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, kRes, sRes] = await Promise.all([
        fetch(getApiEndpoint("/api/problems"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/problems/kedb/list"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/problems/stats"), { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pRes.ok) { const d = await pRes.json(); setProblems(d.problems || []); }
      if (kRes.ok) { const d = await kRes.json(); setKedb(d.known_errors || []); }
      if (sRes.ok) { const d = await sRes.json(); setStats(d.stats || {}); }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadKEDB = useCallback(async () => {
    const res = await fetch(
      getApiEndpoint(`/api/problems/kedb/list${kedbSearch ? `?search=${encodeURIComponent(kedbSearch)}` : ""}`),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.ok) { const d = await res.json(); setKedb(d.known_errors || []); }
  }, [token, kedbSearch]);

  useEffect(() => {
    const t = setTimeout(() => loadKEDB(), 400);
    return () => clearTimeout(t);
  }, [kedbSearch]);

  const handleCreateKE = async () => {
    if (!newKE.title || !newKE.symptoms) {
      toast({ title: "Campos obrigatórios", description: "Título e sintomas são necessários", variant: "destructive" });
      return;
    }
    const res = await fetch(getApiEndpoint("/api/problems/kedb"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(newKE),
    });
    if (res.ok) {
      toast({ title: "Erro conhecido cadastrado", description: `"${newKE.title}" adicionado ao KEDB` });
      setShowNewKE(false);
      setNewKE({ title: "", symptoms: "", root_cause: "", workaround: "", permanent_fix: "", severity: "medium", source_problem: "" });
      loadAll();
    } else {
      toast({ title: "Erro", description: "Não foi possível cadastrar", variant: "destructive" });
    }
  };

  const resolveKE = async (id: number) => {
    const res = await fetch(getApiEndpoint(`/api/problems/kedb/${id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: "resolved" }),
    });
    if (res.ok) { toast({ title: "Resolvido", description: "Erro conhecido marcado como resolvido" }); loadAll(); }
  };

  if (!isInternal) return null;

  const filteredKedb = kedbSearch
    ? kedb.filter(k => k.title.toLowerCase().includes(kedbSearch.toLowerCase()) || k.symptoms.toLowerCase().includes(kedbSearch.toLowerCase()))
    : kedb;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BugIcon className="h-6 w-6 text-red-600" /> Gestão de Problemas
            </h1>
            <p className="text-gray-500 text-sm mt-1">RCA, KEDB e análise de causa raiz</p>
          </div>
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCwIcon className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Total", value: stats.total, color: "gray" },
            { label: "Abertos", value: stats.open, color: "blue" },
            { label: "Em andamento", value: stats.in_progress, color: "yellow" },
            { label: "Resolvidos", value: stats.resolved, color: "green" },
            { label: "KEDB ativos", value: stats.kedb_active, color: "orange" },
            { label: "RCA concluídas", value: stats.rca_completed, color: "purple" },
          ].map((m, i) => (
            <Card key={i}>
              <CardContent className="p-3 text-center">
                <p className="text-xs text-gray-500">{m.label}</p>
                <p className={`text-xl font-bold text-${m.color}-600`}>{loading ? "—" : m.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="problems">
          <TabsList className="mb-4">
            <TabsTrigger value="problems">Problemas</TabsTrigger>
            <TabsTrigger value="kedb" className="flex items-center gap-1">
              <DatabaseIcon className="h-3.5 w-3.5" /> KEDB
            </TabsTrigger>
          </TabsList>

          {/* ── Problems ── */}
          <TabsContent value="problems">
            <div className="space-y-3">
              {problems.map(p => (
                <Link key={p.id} to={`/atendimento/chamado/${p.id}`} className="block">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm">{p.title}</p>
                          <span className="text-xs text-gray-400">{p.id}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                          <span>Solicitante: {p.requester_name || "—"}</span>
                          {p.assignee_name && <span>• Atribuído: {p.assignee_name}</span>}
                          <span>• Criado: {fmtDate(p.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`text-xs ${PRIORITY_COLORS[p.priority] || ""} border-0`}>
                          {p.priority === "critical" ? "Crítico" : p.priority === "high" ? "Alto" : p.priority === "medium" ? "Médio" : "Baixo"}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {STATUS_LABELS[p.status] || p.status}
                        </Badge>
                        {p.rca_status ? (
                          <Badge className={`text-xs border-0 ${p.rca_status === "completed" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                            <BookOpenIcon className="h-3 w-3 mr-1" />
                            RCA {p.rca_status === "completed" ? "concluída" : p.rca_status === "in_review" ? "em revisão" : "rascunho"}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-gray-400">
                            Sem RCA
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {!loading && problems.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <BugIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhum problema registrado.</p>
                  <Link to="/novo-chamado">
                    <Button className="mt-3" size="sm">
                      <PlusIcon className="h-4 w-4 mr-1" /> Registrar Problema
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── KEDB ── */}
          <TabsContent value="kedb">
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="relative flex-1 max-w-md">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Buscar por título ou sintoma..."
                  value={kedbSearch}
                  onChange={e => setKedbSearch(e.target.value)}
                />
              </div>
              {isInternal && (
                <Button onClick={() => setShowNewKE(true)} size="sm" className="gap-2">
                  <PlusIcon className="h-4 w-4" /> Novo Erro Conhecido
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {filteredKedb.map(ke => (
                <Card key={ke.id} className={ke.status === "resolved" ? "opacity-70" : ""}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm">{ke.title}</p>
                          {ke.source_problem && (
                            <Link to={`/atendimento/chamado/${ke.source_problem}`}
                              className="text-xs text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>
                              #{ke.source_problem}
                            </Link>
                          )}
                        </div>
                        <div className="space-y-1.5 mt-2">
                          <div>
                            <span className="text-xs font-medium text-gray-500 uppercase">Sintomas</span>
                            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{ke.symptoms}</p>
                          </div>
                          {ke.workaround && (
                            <div>
                              <span className="text-xs font-medium text-green-600 uppercase">Contorno</span>
                              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{ke.workaround}</p>
                            </div>
                          )}
                          {ke.permanent_fix && (
                            <div>
                              <span className="text-xs font-medium text-blue-600 uppercase">Solução definitiva</span>
                              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{ke.permanent_fix}</p>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Cadastrado por {ke.created_by_name || "—"} em {fmtDate(ke.created_at)}
                          {ke.resolved_at && ` • Resolvido em ${fmtDate(ke.resolved_at)}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge className={`text-xs ${SEVERITY_COLORS[ke.severity] || ""} border-0`}>
                          {ke.severity === "critical" ? "Crítico" : ke.severity === "high" ? "Alto" : ke.severity === "medium" ? "Médio" : "Baixo"}
                        </Badge>
                        <Badge className={`text-xs border-0 ${ke.status === "active" ? "bg-red-50 text-red-700" : ke.status === "in_progress" ? "bg-yellow-50 text-yellow-700" : "bg-green-50 text-green-700"}`}>
                          {ke.status === "active" ? "Ativo" : ke.status === "in_progress" ? "Em progresso" : "Resolvido"}
                        </Badge>
                        {ke.status !== "resolved" && isInternal && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7 border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => resolveKE(ke.id)}
                          >
                            <CheckCircleIcon className="h-3.5 w-3.5 mr-1" /> Resolvido
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && filteredKedb.length === 0 && (
                <div className="text-center text-gray-400 py-12">
                  <DatabaseIcon className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>{kedbSearch ? "Nenhum resultado para a busca." : "KEDB vazio. Adicione o primeiro erro conhecido."}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog: Novo Erro Conhecido */}
      <Dialog open={showNewKE} onOpenChange={setShowNewKE}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo Erro Conhecido (KEDB)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Título *</label>
              <Input value={newKE.title} onChange={e => setNewKE(p => ({ ...p, title: e.target.value }))} placeholder="Título do erro conhecido" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Severidade</label>
                <Select value={newKE.severity} onValueChange={v => setNewKE(p => ({ ...p, severity: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Crítica</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Problema de origem</label>
                <Input value={newKE.source_problem} onChange={e => setNewKE(p => ({ ...p, source_problem: e.target.value }))} placeholder="Ex: PRB-2025-001" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Sintomas *</label>
              <Textarea value={newKE.symptoms} onChange={e => setNewKE(p => ({ ...p, symptoms: e.target.value }))} rows={3} placeholder="Descreva os sintomas observados..." />
            </div>
            <div>
              <label className="text-sm font-medium">Causa raiz</label>
              <Textarea value={newKE.root_cause} onChange={e => setNewKE(p => ({ ...p, root_cause: e.target.value }))} rows={2} placeholder="Causa raiz identificada..." />
            </div>
            <div>
              <label className="text-sm font-medium">Contorno imediato</label>
              <Textarea value={newKE.workaround} onChange={e => setNewKE(p => ({ ...p, workaround: e.target.value }))} rows={2} placeholder="Passos para contornar o problema até a solução definitiva..." />
            </div>
            <div>
              <label className="text-sm font-medium">Solução definitiva</label>
              <Textarea value={newKE.permanent_fix} onChange={e => setNewKE(p => ({ ...p, permanent_fix: e.target.value }))} rows={2} placeholder="Solução definitiva planejada ou implementada..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewKE(false)}>Cancelar</Button>
            <Button onClick={handleCreateKE}>Adicionar ao KEDB</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ProblemManagement;
